import { isRequirementCourse } from "./courseAccreditation.js";
import { generateSlugWithId } from "./slugUtils.js";

export const COURSE_CATALOG_SELECT = `
  id,
  title,
  short_description,
  price,
  price_inr,
  price_usd,
  price_cad,
  non_exemplar_price_inr,
  non_exemplar_price_usd,
  non_exemplar_price_cad,
  thumbnail_url,
  duration_hours,
  rating,
  total_ratings,
  total_enrollments,
  credits,
  difficulty_level,
  certification_provided,
  course_types,
  course_categories,
  iso_frameworks,
  type_of_course,
  status,
  is_active,
  is_refresher,
  parent_course_id,
  accredited_available,
  non_accredited_available,
  created_at,
  category:course_categories(
    id,
    name,
    slug
  ),
  course_delivery_modes(
    delivery_mode,
    is_enabled
  )
`;

export const COURSE_LOOKUP_SELECT = "id, title";

const addOptionIfMissing = (options, name, value) => {
  if (!options.some((option) => option.value === value)) {
    options.push({ name, value });
  }
};

// Raw type_of_course tags vary ("ILT", "Live Virtual", "Classroom", …).
// Every tag maps onto one of the three delivery modes so a raw tag can never
// become its own filter that routes learners into a self-paced purchase.
const LIVE_VIRTUAL_TAGS = new Set([
  "instructor_led",
  "ilt",
  "vilt",
  "live",
  "live_virtual",
  "live_online",
  "virtual",
  "virtual_classroom",
  "virtual_instructor_led",
  "instructor_led_live_virtual",
  "online_instructor_led",
  "live_virtual_classroom",
]);
const IN_PERSON_TAGS = new Set([
  "in_person",
  "classroom",
  "in_person_classroom",
  "face_to_face",
  "onsite",
  "on_site",
]);
const SELF_PACED_TAGS = new Set([
  "self_paced",
  "online",
  "online_self_paced",
  "e_learning",
  "elearning",
  "on_demand",
]);

export const getCanonicalDeliveryValue = (type) => {
  const normalizedValue = normalizeCourseFilterValue(type);
  if (!normalizedValue) return "";
  if (LIVE_VIRTUAL_TAGS.has(normalizedValue)) {
    return INSTRUCTOR_LED_DELIVERY_VALUE;
  }
  if (IN_PERSON_TAGS.has(normalizedValue)) return IN_PERSON_DELIVERY_VALUE;
  if (SELF_PACED_TAGS.has(normalizedValue)) return SELF_PACED_DELIVERY_VALUE;
  return "";
};

const DELIVERY_FILTER_LABELS = {
  self_paced: "Self-Paced",
  instructor_led: "Instructor-Led Live Virtual",
  in_person: "In Person Classroom",
};

const getDeliveryFilterOption = (type) => {
  const value = getCanonicalDeliveryValue(type);
  if (!value) return null;
  return { name: DELIVERY_FILTER_LABELS[value], value };
};

const addTrimmedValues = (target, values) => {
  if (!Array.isArray(values)) return;
  values.forEach((value) => {
    if (typeof value !== "string") return;
    const trimmed = value.trim();
    if (trimmed) target.add(trimmed);
  });
};

export const SELF_PACED_DELIVERY_VALUE = "self_paced";
export const INSTRUCTOR_LED_DELIVERY_VALUE = "instructor_led";
export const IN_PERSON_DELIVERY_VALUE = "in_person";
export const PARTNER_STORE_DELIVERY_TYPES = [
  SELF_PACED_DELIVERY_VALUE,
  INSTRUCTOR_LED_DELIVERY_VALUE,
  IN_PERSON_DELIVERY_VALUE,
];

const COURSE_CATALOG_QUERY_FILTER_KEYS = [
  "category",
  "iso_standard",
  "difficulty_level",
  "delivery_type",
  "course_type",
  "payment_type",
];

const getCourseCatalogFilterValue = (filters, key) => {
  switch (key) {
    case "category":
      return filters.category;
    case "iso_standard":
      return filters.iso_standard;
    case "difficulty_level":
      return filters.difficulty_level;
    case "delivery_type":
      return filters.delivery_type;
    case "course_type":
      return filters.course_type;
    case "payment_type":
      return filters.payment_type;
    default:
      throw new Error(`Unsupported course catalog filter: ${key}`);
  }
};

const setCourseCatalogFilterValue = (filters, key, value) => {
  switch (key) {
    case "category":
      filters.category = value;
      return;
    case "iso_standard":
      filters.iso_standard = value;
      return;
    case "difficulty_level":
      filters.difficulty_level = value;
      return;
    case "delivery_type":
      filters.delivery_type = value;
      return;
    case "course_type":
      filters.course_type = value;
      return;
    case "payment_type":
      filters.payment_type = value;
      return;
    default:
      throw new Error(`Unsupported course catalog filter: ${key}`);
  }
};

const getStringParam = (params, key) => {
  const value = params.get(key);
  return typeof value === "string" ? value.trim() : "";
};

export const parseCourseCatalogQueryState = (
  search,
  defaultFilters = {},
  defaultSort = "relevance",
  allowedSorts = null,
) => {
  const params = new URLSearchParams(search || "");
  const filters = { ...defaultFilters };

  COURSE_CATALOG_QUERY_FILTER_KEYS.forEach((key) => {
    const value = getStringParam(params, key);
    if (value || Object.prototype.hasOwnProperty.call(filters, key)) {
      setCourseCatalogFilterValue(filters, key, value);
    }
  });

  if (Object.prototype.hasOwnProperty.call(filters, "rating")) {
    const rating = Number(params.get("rating"));
    filters.rating = Number.isFinite(rating) && rating > 0 ? rating : 0;
  }

  const requestedSort = getStringParam(params, "sort") || defaultSort;
  const sortBy =
    Array.isArray(allowedSorts) && !allowedSorts.includes(requestedSort)
      ? defaultSort
      : requestedSort;

  return {
    searchTerm: getStringParam(params, "search"),
    sortBy,
    filters,
  };
};

export const buildCourseCatalogQuerySearch = (
  { searchTerm = "", sortBy = "relevance", filters = {} },
  defaultFilters = {},
  defaultSort = "relevance",
) => {
  const params = new URLSearchParams();
  const trimmedSearch = typeof searchTerm === "string" ? searchTerm.trim() : "";

  if (trimmedSearch) params.set("search", trimmedSearch);
  if (sortBy && sortBy !== defaultSort) params.set("sort", sortBy);

  COURSE_CATALOG_QUERY_FILTER_KEYS.forEach((key) => {
    const filterValue = getCourseCatalogFilterValue(filters, key);
    const value = typeof filterValue === "string" ? filterValue.trim() : "";
    const defaultFilterValue = getCourseCatalogFilterValue(defaultFilters, key);
    const defaultValue =
      typeof defaultFilterValue === "string" ? defaultFilterValue.trim() : "";
    if (value && value !== defaultValue) params.set(key, value);
  });

  const rating = Number(filters.rating);
  if (Number.isFinite(rating) && rating > 0) {
    params.set("rating", String(rating));
  }

  const serialized = params.toString();
  return serialized ? `?${serialized}` : "";
};

export const normalizeCourseFilterValue = (value) =>
  typeof value === "string"
    ? value.trim().toLowerCase().replace(/[^a-z0-9]/g, "_")
    : "";

export const getCourseCatalogCourseHref = (course, deliveryType) => {
  const normalizedDeliveryType = normalizeCourseFilterValue(deliveryType);

  if (
    [INSTRUCTOR_LED_DELIVERY_VALUE, IN_PERSON_DELIVERY_VALUE].includes(
      normalizedDeliveryType,
    )
  ) {
    const deliveryMode =
      normalizedDeliveryType === IN_PERSON_DELIVERY_VALUE
        ? "in_person"
        : "virtual";
    return `/batches?course_id=${encodeURIComponent(course.id)}&delivery_mode=${deliveryMode}`;
  }

  return `/courses/${generateSlugWithId(course?.title, course?.id)}`;
};

export const getHiddenPartnerStoreDeliveryTypes = (brandingSettings = {}) => {
  const hiddenTypes = Array.isArray(
    brandingSettings?.hidden_course_delivery_types,
  )
    ? brandingSettings.hidden_course_delivery_types
    : [];

  return [
    ...new Set(
      hiddenTypes
        .map(normalizeCourseFilterValue)
        .filter((type) => PARTNER_STORE_DELIVERY_TYPES.includes(type)),
    ),
  ];
};

export const isPartnerStoreDeliveryTypeVisible = (
  brandingSettings,
  deliveryType,
) => {
  const normalizedDeliveryType =
    normalizeCourseFilterValue(deliveryType) || SELF_PACED_DELIVERY_VALUE;

  if (!PARTNER_STORE_DELIVERY_TYPES.includes(normalizedDeliveryType)) {
    return true;
  }

  return !getHiddenPartnerStoreDeliveryTypes(brandingSettings).includes(
    normalizedDeliveryType,
  );
};

const isTransitionTrainingCourse = (course = {}) => {
  const courseTypes = Array.isArray(course?.course_types)
    ? course.course_types
    : [course?.course_types];

  return (
    courseTypes.some(
      (type) => normalizeCourseFilterValue(type) === "transition",
    ) ||
    normalizeCourseFilterValue(course?.title).includes("transition_training")
  );
};

export const buildDeliveryFilterOptions = (types = []) => {
  const baseTypes = types.length > 0 ? types : ["Self-Paced"];
  const options = [];

  baseTypes.forEach((type) => {
    const option = getDeliveryFilterOption(type);
    if (option) addOptionIfMissing(options, option.name, option.value);
  });

  addOptionIfMissing(
    options,
    "Instructor-Led Live Virtual",
    INSTRUCTOR_LED_DELIVERY_VALUE,
  );
  addOptionIfMissing(options, "In Person Classroom", IN_PERSON_DELIVERY_VALUE);

  return options;
};

export const matchesStoredDeliveryType = (course, deliveryType) => {
  const normalizedDeliveryType = normalizeCourseFilterValue(deliveryType);
  if (!normalizedDeliveryType) return true;

  const types = Array.isArray(course?.type_of_course)
    ? course.type_of_course
    : [course?.type_of_course];

  return types.some(
    (type) => getCanonicalDeliveryValue(type) === normalizedDeliveryType,
  );
};

// A course whose self-paced delivery mode is switched off cannot be bought
// self-paced (checkout rejects it). A missing row means "not configured" and
// stays purchasable, matching the servers.
export const isSelfPacedDisabled = (course) =>
  Array.isArray(course?.course_delivery_modes) &&
  course.course_delivery_modes.some(
    (mode) =>
      mode?.delivery_mode === SELF_PACED_DELIVERY_VALUE &&
      mode?.is_enabled === false,
  );

export const shouldShowSelfPacedVariant = (
  deliveryType,
  brandingSettings,
) =>
  isPartnerStoreDeliveryTypeVisible(
    brandingSettings,
    SELF_PACED_DELIVERY_VALUE,
  ) &&
  ![INSTRUCTOR_LED_DELIVERY_VALUE, IN_PERSON_DELIVERY_VALUE].includes(
    normalizeCourseFilterValue(deliveryType),
  );

export const shouldShowInstructorLedVariant = (
  deliveryType,
  course,
  brandingSettings,
) => {
  if (isRequirementCourse(course)) return false;
  if (isTransitionTrainingCourse(course)) return false;
  if (
    !isPartnerStoreDeliveryTypeVisible(
      brandingSettings,
      INSTRUCTOR_LED_DELIVERY_VALUE,
    )
  ) {
    return false;
  }

  const normalizedDeliveryType = normalizeCourseFilterValue(deliveryType);
  return (
    !normalizedDeliveryType ||
    normalizedDeliveryType === INSTRUCTOR_LED_DELIVERY_VALUE
  );
};

export const shouldShowInPersonVariant = (
  deliveryType,
  course,
  brandingSettings,
) => {
  if (isRequirementCourse(course)) return false;
  if (isTransitionTrainingCourse(course)) return false;
  if (
    !isPartnerStoreDeliveryTypeVisible(
      brandingSettings,
      IN_PERSON_DELIVERY_VALUE,
    )
  ) {
    return false;
  }

  const normalizedDeliveryType = normalizeCourseFilterValue(deliveryType);
  return (
    !normalizedDeliveryType || normalizedDeliveryType === IN_PERSON_DELIVERY_VALUE
  );
};

export const shouldApplyStoredDeliveryFilter = (deliveryType) => {
  const normalizedDeliveryType = normalizeCourseFilterValue(deliveryType);
  return (
    Boolean(normalizedDeliveryType) &&
    ![INSTRUCTOR_LED_DELIVERY_VALUE, IN_PERSON_DELIVERY_VALUE].includes(
      normalizedDeliveryType,
    )
  );
};

export const getVisibleCourseVariantCount = (
  items = [],
  deliveryType,
  brandingSettings,
) =>
  items.reduce((count, item) => {
    const course = item?.course || item;
    return (
      count +
      Number(shouldShowSelfPacedVariant(deliveryType, brandingSettings)) +
      Number(
        shouldShowInstructorLedVariant(deliveryType, course, brandingSettings),
      ) +
      Number(shouldShowInPersonVariant(deliveryType, course, brandingSettings))
    );
  }, 0);

export const buildCourseFilterOptions = (courses = []) => {
  const courseTypes = new Set();
  const deliveryTypes = new Set();

  courses.forEach((course) => {
    addTrimmedValues(courseTypes, course?.course_types);
    addTrimmedValues(deliveryTypes, course?.type_of_course);
  });

  return {
    courseTypes: Array.from(courseTypes).sort((a, b) => a.localeCompare(b)),
    deliveryTypes: Array.from(deliveryTypes).sort((a, b) =>
      a.localeCompare(b),
    ),
  };
};
